import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { User } from "firebase/auth";
import { auth, signInWithGoogle, signOutUser } from "./firebase";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import NewsListPage from "./components/news/NewsListPage";
import ArticleDetail from './components/news/ArticleDetail';
import ProfilePage from './components/profile/ProfilePage';
import AttendancePage from './components/attendance/AttendancePage';
import AttendanceList from './components/attendance/AttendanceList';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import EmailVerificationPage from './components/auth/EmailVerificationPage';
import ReimbursementForm from './components/reimbursement/ReimbursementForm';
import ReimbursementDetail from './components/reimbursement/ReimbursementDetail';
import AllReimbursements from './components/reimbursement/AllReimbursements';
import HomePage from './components/home/HomePage';
import BackToTop from './components/layout/BackToTop';
import ProgramsListPage from './components/programs/ProgramsListPage';
import ProgramDetail from './components/programs/ProgramDetail';
import AllTeacherCourses from './components/teacher/AllTeacherCourses';
import CourseStudents from './components/teacher/CourseStudents';
import { HeaderProvider } from './contexts/HeaderContext';
import './i18n';
import RegistrationForm from './components/registration/RegistrationForm';
import RegistrationSuccess from './components/registration/RegistrationSuccess';
import AboutPage from './components/about/AboutPage';
import CalendarPage from './components/calendar/CalendarPage';
import TeachersPage from './components/teachers/TeachersPage';
import ContactPage from './components/contact/ContactPage';
import DonatePage from './components/donate/DonatePage';
import TeacherAttendanceDetail from './components/profile/TeacherAttendanceDetail';

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const location = useLocation();

    // 路由变化时自动滚动到顶部
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
            setIsLoggedIn(!!user);
            setUserEmail(user?.email || null);
        });

        return () => unsubscribe();
    }, []);

    const handleLogin = useCallback(async () => {
        try {
            await signInWithGoogle();
        } catch (error) {
            console.error('Login error:', error);
        }
    }, []);

    const handleLogout = useCallback(async () => {
        try {
            await signOutUser();
        } catch (error) {
            console.error('Logout error:', error);
        }
    }, []);

    // 判断当前路径是否需要显示 Header 和 Footer
    const shouldShowHeaderAndFooter = () => {
        // 不显示 Header 和 Footer 的路径列表
        const excludePaths = [
            '/attendance',  // 精确匹配
            '/attendance/', // 带斜杠的匹配
        ];
        
        // 检查是否以 /attendance/ 开头（用于匹配所有签到相关页面）
        if (location.pathname.startsWith('/attendance/')) {
            return false;
        }

        return !excludePaths.includes(location.pathname);
    };

    return (
        <HeaderProvider>
            <div className="flex flex-col min-h-screen">
                {shouldShowHeaderAndFooter() && (
                    <Header 
                        isLoggedIn={isLoggedIn} 
                        userEmail={userEmail} 
                        onLogout={handleLogout} 
                    />
                )}
                <main className="flex-grow">
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/news" element={<NewsListPage />} />
                        <Route path="/programs" element={<ProgramsListPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/attendance" element={<AttendancePage />} />
                        <Route path="/attendance/:courseId" element={<AttendancePage />} />
                        <Route path="/attendancelist" element={<AttendanceList />} />
                        <Route path="/article/:id" element={<ArticleDetail />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/verify-email" element={<EmailVerificationPage />} />
                        <Route path="/reimbursement/new" element={<ReimbursementForm />} />
                        <Route path="/reimbursement/:id" element={<ReimbursementDetail />} />
                        <Route path="/profile/reimbursements/new" element={<ReimbursementForm />} />
                        <Route path="/profile/reimbursements/:id" element={<ReimbursementDetail />} />
                        <Route path="/profile/reimbursements/edit/:id" element={<ReimbursementForm />} />
                        <Route path="/profile/reimbursements/all" element={<AllReimbursements />} />
                        <Route path="/program/:id" element={<ProgramDetail />} />
                        <Route path="/registration" element={<RegistrationForm />} />
                        <Route path="/registration/new" element={<RegistrationForm />} />
                        <Route path="/registration/success" element={<RegistrationSuccess />} />
                        <Route path="/teacher/courses" element={<AllTeacherCourses />} />
                        <Route path="/students" element={<CourseStudents />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/calendar" element={<CalendarPage />} />
                        <Route path="/teachers" element={<TeachersPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/donate" element={<DonatePage />} />
                        <Route path="/teacher-attendance-detail" element={<TeacherAttendanceDetail />} />
                    </Routes>
                </main>
                {shouldShowHeaderAndFooter() && <Footer />}
                <BackToTop />
            </div>
        </HeaderProvider>
    );
}

export default App;